import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import { 
  Table, 
  Button, 
  Modal, 
  Input, 
  Select, 
  DatePicker, 
  Space, 
  Popconfirm, 
  message,
  Card,
  Row,
  Col,
  Tag,
  Typography
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  SearchOutlined 
} from '@ant-design/icons';

const { Option } = Select;

const HullData = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [selectedEventType, setSelectedEventType] = useState(null);
  const [loading, setLoading] = useState(false);

  // Available filter options
  const dateOptions = [
    '2023-01-15', '2023-02-20', '2023-03-10', '2023-04-05', 
    '2023-06-20', '2024-01-05', '2024-03-15', '2024-06-12', 
    '2024-09-08', '2025-01-20'
  ];

  const timeOptions = [
    '06:00', '06:30', '06:45', '07:15', '08:00', '08:30', 
    '09:00', '10:15', '12:00', '13:30', '14:15', '15:45', 
    '16:00', '17:30', '18:00', '19:15', '20:00', '21:30'
  ];

  const eventTypeOptions = [
    'Departure', 'Arrival', 'Maintenance', 'Inspection', 
    'Loading', 'Unloading', 'Anchoring', 'Berthing'
  ];

  // Sample initial data with date, time, and eventType
  useEffect(() => {
    const sampleData = [
      {
        id: 1,
        imoNumber: 'IMO9123456',
        date: '2023-01-15',
        time: '08:00',
        eventType: 'Departure',
        vesselName: 'Ocean Explorer',
        hullType: 'Cargo',
        constructionDate: '2023-01-15',
        status: 'Active',
        length: '150',
        width: '25',
        draft: '8.5',
        tonnage: '5000',
        material: 'Steel',
        notes: 'Standard cargo vessel - departure from berth'
      },
      {
        id: 2,
        imoNumber: 'IMO9234567',
        date: '2023-06-20',
        time: '14:15',
        eventType: 'Arrival',
        vesselName: 'Sea Navigator',
        hullType: 'Passenger',
        constructionDate: '2023-06-20',
        status: 'Under Construction',
        length: '120',
        width: '20',
        draft: '6.0',
        tonnage: '3000',
        material: 'Aluminum',
        notes: 'Passenger ferry - arrival at port'
      },
      {
        id: 3,
        imoNumber: 'IMO9345678',
        date: '2023-03-10',
        time: '06:30',
        eventType: 'Departure',
        vesselName: 'Deep Fisher',
        hullType: 'Fishing',
        constructionDate: '2023-03-10',
        status: 'Active',
        length: '80',
        width: '15',
        draft: '4.5',
        tonnage: '1500',
        material: 'Fiberglass',
        notes: 'Commercial fishing vessel - early morning departure'
      },
      {
        id: 4,
        imoNumber: 'IMO9456789',
        date: '2024-01-05',
        time: '16:00',
        eventType: 'Maintenance',
        vesselName: 'Coast Guardian',
        hullType: 'Naval',
        constructionDate: '2024-01-05',
        status: 'Maintenance',
        length: '180',
        width: '28',
        draft: '9.0',
        tonnage: '7500',
        material: 'Steel',
        notes: 'Naval patrol vessel - scheduled maintenance'
      }
    ];
    setData(sampleData);
    setFilteredData(sampleData);
  }, []);

  // Filter data based on selected date, time, and event type
  const applyFilters = () => {
    let filtered = data;

    if (selectedDate) {
      filtered = filtered.filter(record => record.date === selectedDate);
    }

    if (selectedTime) {
      filtered = filtered.filter(record => record.time === selectedTime);
    }

    if (selectedEventType) {
      filtered = filtered.filter(record => record.eventType === selectedEventType);
    }

    setFilteredData(filtered);
  };

  // Apply filters whenever filter values change
  useEffect(() => {
    applyFilters();
  }, [selectedDate, selectedTime, selectedEventType, data]);

  // Clear all filters
  const clearFilters = () => {
    setSelectedDate(null);
    setSelectedTime(null);
    setSelectedEventType(null);
    setFilteredData(data);
  };

  // Open modal for create/edit
  const showModal = (record = null) => {
    setEditingRecord(record);
    setIsModalVisible(true);
  };

  // Close modal
  const handleCancel = () => {
    setIsModalVisible(false);
    setEditingRecord(null);
  };

  // Create new record based on selected filters
  const createNewRecord = () => {
    // Check if exact match exists
    const existingRecord = data.find(record => 
      record.date === selectedDate && 
      record.time === selectedTime && 
      record.eventType === selectedEventType
    );

    if (existingRecord) {
      // Edit existing record
      showModal(existingRecord);
      message.info('Record found! Opening for editing.');
    } else {
      // Create new record with pre-filled filter values
      const newRecord = {
        date: selectedDate,
        time: selectedTime,
        eventType: selectedEventType
      };
      showModal(newRecord);
      message.info('No matching record found. Creating new hull data.');
    }
  };

  // Handle form submission
  const handleFormSubmit = () => {
    // Collect form data from inputs
    const imoNumberEl = document.getElementById('imoNumber');
    const vesselNameEl = document.getElementById('vesselName');
    const lengthEl = document.getElementById('length');
    const widthEl = document.getElementById('width');
    const draftEl = document.getElementById('draft');
    const tonnageEl = document.getElementById('tonnage');
    const notesEl = document.getElementById('notes');

    // Get select values
    const dateSelect = document.querySelector('#date .ant-select-selection-item');
    const timeSelect = document.querySelector('#time .ant-select-selection-item');
    const eventTypeSelect = document.querySelector('#eventType .ant-select-selection-item');
    const hullTypeSelect = document.querySelector('#hullType .ant-select-selection-item');
    const statusSelect = document.querySelector('#status .ant-select-selection-item');
    const materialSelect = document.querySelector('#material .ant-select-selection-item');

    const formData = {
      imoNumber: imoNumberEl?.value || '',
      date: dateSelect?.textContent || editingRecord?.date || '',
      time: timeSelect?.textContent || editingRecord?.time || '',
      eventType: eventTypeSelect?.textContent || editingRecord?.eventType || '',
      vesselName: vesselNameEl?.value || '',
      hullType: hullTypeSelect?.textContent || editingRecord?.hullType || '',
      constructionDate: editingRecord?.constructionDate || '',
      status: statusSelect?.textContent || editingRecord?.status || 'Active',
      length: lengthEl?.value || '',
      width: widthEl?.value || '',
      draft: draftEl?.value || '',
      tonnage: tonnageEl?.value || '',
      material: materialSelect?.textContent || editingRecord?.material || '',
      notes: notesEl?.value || ''
    };

    // Basic validation
    if (!formData.imoNumber || !formData.date || !formData.time || !formData.eventType || !formData.vesselName || !formData.hullType) {
      message.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    
    setTimeout(() => {
      const processedData = {
        ...formData,
        id: editingRecord?.id || Date.now()
      };

      if (editingRecord?.id) {
        // Update existing record
        const updatedData = data.map(item => 
          item.id === editingRecord.id ? processedData : item
        );
        setData(updatedData);
        message.success('Hull data updated successfully!');
      } else {
        // Create new record
        const newData = [...data, processedData];
        setData(newData);
        message.success('Hull data created successfully!');
      }

      setLoading(false);
      setIsModalVisible(false);
      setEditingRecord(null);
    }, 1000);
  };

  // Delete record
  const handleDelete = (id) => {
    const updatedData = data.filter(item => item.id !== id);
    setData(updatedData);
    message.success('Hull data deleted successfully!');
  };

  // Get status color for tags
  const getStatusColor = (status) => {
    const colors = {
      'Active': 'green',
      'Under Construction': 'blue',
      'Retired': 'default',
      'Maintenance': 'orange'
    };
    return colors[status] || 'default';
  };

  // Get event type color for tags
  const getEventTypeColor = (eventType) => {
    const colors = {
      'Departure': 'blue',
      'Arrival': 'green',
      'Maintenance': 'orange',
      'Inspection': 'purple',
      'Loading': 'cyan',
      'Unloading': 'geekblue',
      'Anchoring': 'gold',
      'Berthing': 'lime'
    };
    return colors[eventType] || 'default';
  };

  // Table columns
  const columns = [
    {
      title: 'IMO Number',
      dataIndex: 'imoNumber',
      key: 'imoNumber',
      sorter: (a, b) => (a.imoNumber || '').localeCompare(b.imoNumber || ''),
      width: 130,
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      sorter: (a, b) => (a.date || '').localeCompare(b.date || ''),
      width: 120,
    },
    {
      title: 'Time',
      dataIndex: 'time',
      key: 'time',
      width: 80,
    },
    {
      title: 'Event Type',
      dataIndex: 'eventType',
      key: 'eventType',
      render: (eventType) => (
        <Tag color={getEventTypeColor(eventType)}>{eventType}</Tag>
      ),
      width: 120,
    },
    {
      title: 'Vessel Name',
      dataIndex: 'vesselName',
      key: 'vesselName',
      sorter: (a, b) => (a.vesselName || '').localeCompare(b.vesselName || ''),
      width: 140,
    },
    {
      title: 'Hull Type',
      dataIndex: 'hullType',
      key: 'hullType',
      filters: [
        { text: 'Cargo', value: 'Cargo' },
        { text: 'Passenger', value: 'Passenger' },
        { text: 'Fishing', value: 'Fishing' },
        { text: 'Naval', value: 'Naval' },
        { text: 'Recreational', value: 'Recreational' },
      ],
      onFilter: (value, record) => record.hullType === value,
      width: 100,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      filters: [
        { text: 'Active', value: 'Active' },
        { text: 'Under Construction', value: 'Under Construction' },
        { text: 'Retired', value: 'Retired' },
        { text: 'Maintenance', value: 'Maintenance' },
      ],
      onFilter: (value, record) => record.status === value,
      render: (status) => <Tag color={getStatusColor(status)}>{status}</Tag>,
      width: 120,
    },
    {
      title: 'Dimensions (L×W×D)',
      key: 'dimensions',
      render: (_, record) => `${record.length || 0}×${record.width || 0}×${record.draft || 0}m`,
      width: 140,
    },
    {
      title: 'Tonnage',
      dataIndex: 'tonnage',
      key: 'tonnage',
      render: (tonnage) => `${tonnage || 0} tons`,
      sorter: (a, b) => parseInt(a.tonnage || 0) - parseInt(b.tonnage || 0),
      width: 100,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="primary"
            size="small"
            icon={<EditOutlined />}
            onClick={() => showModal(record)}
          />
          <Popconfirm
            title="Are you sure you want to delete this hull data?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
            okType="danger"
          >
            <Button
              danger
              size="small"
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];
const dateTimeOptions =[]
  return (
    <div className='p-1'>       
     <div style={{ display: 'flex', alignItems: 'center',   }}>
            <SearchOutlined style={{fontSize:"16px"}}/>
                        <Typography.Title level={4} className="mt-2 ms-2">Engine Predictives Dashboard</Typography.Title>

          </div>
        {/* Filter Controls */}
        <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
          <Col xs={24} sm={6} lg={4}>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Date:
              </label>
              <Select
                placeholder="Select date"
                style={{ width: '100%' }}
                value={selectedDate}
                onChange={setSelectedDate}
                allowClear
                showSearch
              >
                {dateOptions.map(date => (
                  <Option key={date} value={date}>{date}</Option>
                ))}
              </Select>
            </div>
          </Col>
          
          <Col xs={24} sm={6} lg={4}>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Time:
              </label>
              <Select
                placeholder="Select time"
                style={{ width: '100%' }}
                value={selectedTime}
                onChange={setSelectedTime}
                allowClear
                showSearch
              >
                {timeOptions.map(time => (
                  <Option key={time} value={time}>{time}</Option>
                ))}
              </Select>
            </div>
          </Col>

          <Col xs={24} sm={6} lg={5}>
            <div>
              <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                Event Type:
              </label>
              <Select
                placeholder="Select event type"
                style={{ width: '100%' }}
                value={selectedEventType}
                onChange={setSelectedEventType}
                allowClear
              >
                {eventTypeOptions.map(eventType => (
                  <Option key={eventType} value={eventType}>{eventType}</Option>
                ))}
              </Select>
            </div>
          </Col>

          <Col xs={24} sm={6} lg={4} style={{ display: 'flex', alignItems: 'end', gap: '8px' }}>
            <Button onClick={clearFilters}>Clear Filters</Button>
          </Col>

          <Col xs={24} sm={12} lg={4} style={{ display: 'flex', alignItems: 'end' }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={createNewRecord}
              disabled={!selectedDate || !selectedTime || !selectedEventType}
              style={{ 
                backgroundColor: '#52c41a', 
                borderColor: '#52c41a',
                fontWeight: '500' 
              }}
            >
              Create/Edit Hull Data
            </Button>
          </Col>

          <Col xs={24} sm={12} lg={3} style={{ display: 'flex', alignItems: 'end', justifyContent: 'flex-end' }}>
            <div style={{ fontSize: '14px', color: '#666' }}>
              Records: <strong>{filteredData.length}</strong>
            </div>
          </Col>
        </Row>

        {/* Info message */}
        {selectedDate || selectedTime || selectedEventType ? (
          <div style={{ 
            marginBottom: '16px', 
            padding: '12px', 
            backgroundColor: '#e6f7ff', 
            border: '1px solid #91d5ff', 
            borderRadius: '6px',
            fontSize: '14px'
          }}>
            <strong>Filter Applied:</strong> 
            {selectedDate && ` Date: ${selectedDate}`}
            {selectedTime && ` | Time: ${selectedTime}`}
            {selectedEventType && ` | Event: ${selectedEventType}`}
            {filteredData.length === 0 && (
              <span style={{ color: '#faad14', marginLeft: '10px' }}>
                No matching records found. Use "Create/Edit Hull Data" to add new record.
              </span>
            )}
          </div>
        ) : null}

        {/* Data Table */}
        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} items`,
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
          scroll={{ x: 1200 }}
          style={{ backgroundColor: '#fff' }}
          bordered
          size="middle"
        />

        {/* Create/Edit Modal */}
        <Modal
          title={
            <div style={{ fontSize: '18px', fontWeight: 'bold' }}>
              {editingRecord?.id ? "Edit Hull Data" : "Add New Hull Data"}
            </div>
          }
          open={isModalVisible}
          onCancel={handleCancel}
          footer={null}
          width={900}
          destroyOnClose
          maskClosable={false}
        >
          <div style={{ padding: '20px 0' }}>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12}>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    IMO Number <span style={{ color: 'red' }}>*</span>
                  </label>
                  <Input 
                    placeholder="Enter IMO number" 
                    size="large"
                    defaultValue={editingRecord?.imoNumber}
                    id="imoNumber"
                  />
                </div>
              </Col>

              <Col xs={24} sm={12}>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Date & Time <span style={{ color: 'red' }}>*</span>
                  </label>
                  <Select 
                    placeholder="Select date & time" 
                    size="large" 
                    showSearch
                    defaultValue={editingRecord?.dateTime}
                    id="dateTime"
                    style={{ width: '100%' }}
                  >
                    {(dateTimeOptions && dateTimeOptions?.length) ? dateTimeOptions.map(dateTime => (
                      <Option key={dateTime} value={dateTime}>{dateTime}</Option>
                    )):[]}
                  </Select>
                </div>
              </Col>

              <Col xs={24} sm={12}>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Event Type
                  </label>
                  <Input 
                    size="large"
                    value="Departure from Berth"
                    disabled
                    style={{ backgroundColor: '#f5f5f5' }}
                  />
                </div>
              </Col>

              <Col xs={24} sm={12}>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Vessel Name <span style={{ color: 'red' }}>*</span>
                  </label>
                  <Input 
                    placeholder="Enter vessel name" 
                    size="large"
                    defaultValue={editingRecord?.vesselName}
                    id="vesselName"
                  />
                </div>
              </Col>

              <Col xs={24} sm={12}>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Hull Type <span style={{ color: 'red' }}>*</span>
                  </label>
                  <Select 
                    placeholder="Select hull type" 
                    size="large"
                    defaultValue={editingRecord?.hullType}
                    id="hullType"
                    style={{ width: '100%' }}
                  >
                    <Option value="Cargo">Cargo</Option>
                    <Option value="Passenger">Passenger</Option>
                    <Option value="Fishing">Fishing</Option>
                    <Option value="Naval">Naval</Option>
                    <Option value="Recreational">Recreational</Option>
                  </Select>
                </div>
              </Col>

              <Col xs={24} sm={12}>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Construction Date
                  </label>
                  <DatePicker
                    placeholder="Select construction date"
                    style={{ width: '100%' }}
                    format="YYYY-MM-DD"
                    size="large"
                    defaultValue={editingRecord?.constructionDate ? dayjs(editingRecord.constructionDate) : null}
                    id="constructionDate"
                  />
                </div>
              </Col>

              <Col xs={24} sm={12}>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Status
                  </label>
                  <Select 
                    size="large"
                    defaultValue={editingRecord?.status || 'Active'}
                    id="status"
                    style={{ width: '100%' }}
                  >
                    <Option value="Active">Active</Option>
                    <Option value="Under Construction">Under Construction</Option>
                    <Option value="Retired">Retired</Option>
                    <Option value="Maintenance">Maintenance</Option>
                  </Select>
                </div>
              </Col>

              <Col xs={24} sm={8}>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Length (m)
                  </label>
                  <Input 
                    type="number" 
                    placeholder="Length" 
                    step="0.1" 
                    size="large"
                    defaultValue={editingRecord?.length}
                    id="length"
                  />
                </div>
              </Col>

              <Col xs={24} sm={8}>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Width (m)
                  </label>
                  <Input 
                    type="number" 
                    placeholder="Width" 
                    step="0.1" 
                    size="large"
                    defaultValue={editingRecord?.width}
                    id="width"
                  />
                </div>
              </Col>

              <Col xs={24} sm={8}>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Draft (m)
                  </label>
                  <Input 
                    type="number" 
                    placeholder="Draft" 
                    step="0.1" 
                    size="large"
                    defaultValue={editingRecord?.draft}
                    id="draft"
                  />
                </div>
              </Col>

              <Col xs={24} sm={12}>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Tonnage
                  </label>
                  <Input 
                    type="number" 
                    placeholder="Tonnage" 
                    size="large"
                    defaultValue={editingRecord?.tonnage}
                    id="tonnage"
                  />
                </div>
              </Col>

              <Col xs={24} sm={12}>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Material
                  </label>
                  <Select 
                    placeholder="Select material" 
                    size="large"
                    defaultValue={editingRecord?.material}
                    id="material"
                    style={{ width: '100%' }}
                  >
                    <Option value="Steel">Steel</Option>
                    <Option value="Aluminum">Aluminum</Option>
                    <Option value="Fiberglass">Fiberglass</Option>
                    <Option value="Wood">Wood</Option>
                    <Option value="Carbon Fiber">Carbon Fiber</Option>
                  </Select>
                </div>
              </Col>

              <Col xs={24}>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Notes
                  </label>
                  <Input.TextArea
                    placeholder="Additional notes..."
                    rows={3}
                    size="large"
                    defaultValue={editingRecord?.notes}
                    id="notes"
                  />
                </div>
              </Col>
            </Row>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '24px' }}>
              <Button onClick={handleCancel} size="large">
                Cancel
              </Button>
              <Button 
                type="primary" 
                onClick={handleFormSubmit}
                loading={loading}
                size="large"
              >
                {editingRecord?.id ? 'Update' : 'Create'} Hull Data
              </Button>
            </div>
          </div>
        </Modal>
      
     </div>

  );
};

export default HullData;